// External libraries
import { Component, createRef } from "react";

// FontAwesome icons
import {
  faDownload,
  faMagnifyingGlass,
  faPaperclip,
  faXmark,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { Hint, Label } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";
import { Upload } from "@progress/kendo-react-upload";

// Internal components
import { ColorChangingIcon } from "../../Common/UI/ColorChangingIcon";
import { Else, If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { Invisible } from "../../Utilities/EntryUtils";
import { fileIconMap } from "./FileTypeIconMap";

// Signals
import { blobServiceClient, envVariables, userEmail } from "../../Common/Store/CommonSignals";
import {
  clearEntryFields,
  entryValues,
  filePreviewUrl,
  readOnlyEntry,
  showFilePreview,
  template,
  templateId,
} from "../../Entry/Manage/EditEntrySignals";

function buildContainerName(branch, id) {
  return `${branch}-${id}`
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 63);
}

export class FileUploadField extends Component {
  inputRef = createRef();

  constructor({
    field,
    value,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    eventIndex,
    galleryParentName,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      files: JSON.tryParse(value, []),
      invisible: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.label = properties?.Label || `${fieldName}:`;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.isPrintPreview = isPrintPreview;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.visibilityRules) this.handleDependencyEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      JSON.stringify(this.state.files) !== JSON.stringify(nextState.files) ||
      this.state.invisible !== nextState.invisible
    );
  }

  componentWillUnmount() {
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
  }

  componentDidUpdate() {
    if (this.state.invisible) {
      const files = this.state.files;

      for (const file of files) {
        this.deleteBlob(file);
      }
      this.clearField();
    }
  }
  //#endregion lifecycle functions

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
      });
    });
  }

  //#region custom functions
  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value && entryValues.value[this.fieldName] !== "[]") {
        this.handleFieldChange({
          e: {
            value: JSON.stringify([]),
          },
          sectionIndex: this.sectionIndex,
          fieldIndex: this.fieldIndex,
        });
        this.setState({
          files: [],
        });
      }
    });
  }

  getFilePreviewUrl = (uid) => {
    const file = this.state.files.filter((file) => file.uid === uid)[0];
    const containerName = buildContainerName(envVariables.value.REACT_APP_AZDEVOPS_BRANCH, templateId.value);
    const containerClient = blobServiceClient.value.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(file.blobName);
    filePreviewUrl.value = blockBlobClient.url;
  };

  async handleDownload(e, uid) {
    e.preventDefault();

    const file = JSON.tryParse(entryValues.value[this.fieldName]).filter(
      (file) => file.uid === uid
    )[0];

    // Create a blockBlobClient for the specific file
    const containerName = buildContainerName(envVariables.value.REACT_APP_AZDEVOPS_BRANCH, templateId.value);

    const containerClient = blobServiceClient.value.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(file.blobName);

    try {
      // Check if the blob exists
      const blobExists = await blockBlobClient.exists();
      if (!blobExists) {
        console.error("Blob does not exist:", file.blobName);
        return;
      }

      // Download blob
      const downloadBlockBlobResponse = await blockBlobClient.download();
      const blobURL = URL.createObjectURL(await downloadBlockBlobResponse.blobBody);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = blobURL;
      link.download = file.name; // Assuming the 'name' attribute holds the file name
      document.body.appendChild(link); // Append link to body to make it work in Firefox
      link.click();
      link.remove(); // Clean up

      // Revoke the URL after a timeout
      setTimeout(() => {
        URL.revokeObjectURL(blobURL);
      }, 100);
    } catch (error) {
      console.error("Error downloading blob:", error);
    }
  }

  CustomListItemUI = (props) => {
    const formatFileSize = (size) => {
      const units = ["B", "KB", "MB", "GB"];
      let index = 0;
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024;
        index++;
      }
      return `${size.toFixed(2)} ${units[index]}`;
    };

    const fileIcon = (extension) => {
      if (fileIconMap[extension]) {
        return fileIconMap[extension]();
      }
      return fileIconMap["any"]();
    };

    return (
      <ul className="k-upload-files k-w-full">
        {props.files.map((file) => {
          return (
            <li key={file.uid}>
              <div className="k-file k-d-flex k-justify-content-between">
                <div className="k-d-flex k-align-items-center">
                  <div>{fileIcon(file.extension)}</div>
                  <div className="k-file-name">
                    {file.name}
                    <If condition={file.validationErrors?.includes("invalidMaxFileSize")}>
                      <div className="required">Error: File size is too large. Max 25MB</div>
                    </If>
                  </div>
                  <div className="k-file-size">({formatFileSize(file.size)})</div>
                </div>
                <div className="k-d-flex k-align-items-center">
                  <div className="k-d-flex k-flex-row">
                    <If
                      condition={
                        file.extension === ".jpg" ||
                        file.extension === ".png" ||
                        file.extension === ".bmp" ||
                        file.extension === ".jpeg"
                      }
                    >
                      <ColorChangingIcon
                        className="k-mr-2"
                        icon={faMagnifyingGlass}
                        initialPrimaryColor="#adadac"
                        initialSecondaryColor="#323130"
                        onClick={() => {
                          showFilePreview.value = true;
                          this.getFilePreviewUrl(file.uid);
                        }}
                      />
                    </If>
                    <ColorChangingIcon
                      className="k-mr-2"
                      icon={faDownload}
                      onClick={(e) => this.handleDownload(e, file.uid)}
                      initialPrimaryColor="#adadac"
                      initialSecondaryColor="#323130"
                    />
                    <ColorChangingIcon
                      icon={faXmark}
                      initialPrimaryColor="#D6562A"
                      onClick={(e) => this.onRemove(e, file.uid)}
                      disabled={readOnlyEntry.value}
                    />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  onAdd = (event) => {
    this.setState((prevState) => ({
      files: [...prevState.files, ...event.affectedFiles],
    }));
  };

  onRemove = (e, uid) => {
    e.preventDefault();
    const file = this.state.files.find((file) => file.uid === uid);

    this.setState((prevState) => ({
      files: prevState.files.filter((file) => file.uid !== uid),
    }));

    let existingFiles = [];
    if (entryValues.value[this.fieldName]) {
      existingFiles = JSON.tryParse(entryValues.value[this.fieldName], []);
    }

    existingFiles = existingFiles.filter((file) => file.uid !== uid);

    this.handleFieldChange({
      e: {
        value: JSON.stringify(existingFiles),
      },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
    });
    this.deleteBlob(file);
  };

  onProgress = (event) => {
    this.setState({ files: event.newState });
  };

  onStatusChange = (event) => {
    this.setState({ files: event.newState });
  };

  async uploadFileToBlob({ file }) {
    try {
      const containerName = buildContainerName(envVariables.value.REACT_APP_AZDEVOPS_BRANCH, templateId.value);
      const containerClient = blobServiceClient.value.getContainerClient(containerName);
      await containerClient.createIfNotExists();

      const blockBlobClient = containerClient.getBlockBlobClient(file.blobName);

      // Ensure getRawFile is called to retrieve the actual file object
      const fileInfo = file.getRawFile();

      // Read the file as an array buffer if it's not being handled correctly
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(fileInfo);
      });

      const uploadOptions = {
        metadata: {
          originalName: file.name,
          fileSize: fileInfo.size.toString(),
          fileType: fileInfo.type,
          templateId: templateId.value,
          templateName: template.value.name,
          templateCompany: template.value.company,
          templateBranch: template.value.branch,
          templateDepartment: template.value.department,
          templateArea: template.value.area,
          uploadedBy: userEmail.value,
        },
      };

      // Upload the file
      const uploadBlobResponse = await blockBlobClient.uploadData(arrayBuffer, uploadOptions);
      console.log(`Upload block blob ${fileInfo.name} successfully`, uploadBlobResponse);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  }

  async deleteBlob(file) {
    const containerName = buildContainerName(envVariables.value.REACT_APP_AZDEVOPS_BRANCH, templateId.value);
    const containerClient = blobServiceClient.value?.getContainerClient(containerName);
    const blockBlobClient = containerClient?.getBlockBlobClient(file.blobName);

    try {
      await blockBlobClient.delete();
      console.log(`Blob ${file.blobName} successfully deleted.`);
    } catch (error) {
      console.error("Error deleting blob:", error);
    }
  }

  clearField() {
    this.setState({
      files: [],
    });

    this.handleFieldChange({
      e: {
        value: JSON.stringify([]),
      },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
    });
  }

  async onSaveRequest(files) {
    const currentFile = files[0];
    const uid = currentFile.uid;
    return new Promise((resolve, reject) => {
      if (currentFile.validationErrors && currentFile.validationErrors.length > 0) {
        reject(new Error({ uid: uid }));
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result && typeof reader.result === "string") {
            const fileInfo = {
              ...currentFile,
              blobName: new Date().getTime() + "_" + currentFile.name,
              status: 4,
            };
            this.uploadFileToBlob({
              file: fileInfo,
            });

            // Retrieve existing files from entryValues
            let existingFiles = [];
            if (entryValues.value[this.fieldName]) {
              existingFiles = JSON.tryParse(entryValues.value[this.fieldName], []);
            }

            // Add the new file info to the existing files
            existingFiles.push(fileInfo);

            this.handleFieldChange({
              e: {
                value: JSON.stringify(existingFiles),
              },
              sectionIndex: this.sectionIndex,
              fieldIndex: this.fieldIndex,
            });

            this.setState({ files: existingFiles });
            resolve({ uid: uid });
          } else {
            reject(new Error({ uid: uid }));
          }
        };
        reader.readAsDataURL(currentFile.getRawFile());
      }
    });
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;

    if (this.isPrintPreview) {
      return (
        <div className="align-center">
          <Label
            style={{ fontSize: "14px", fontWeight: "600" }}
            id={this.fieldName + "_label"}
          >
            {this.fieldName}:
          </Label>

          <If condition={Array.isArray(this.state.files) && this.state.files.length > 0}>
            <FontAwesomeIcon
              data-testid={this.fieldName + "_" + this.fieldIndex}
              icon={faPaperclip}
              style={{ marginLeft: "5px", marginBottom: "8px" }}
            />
            <Else>
              <span style={{ marginLeft: "5px", marginBottom: "8px" }}>No Attachments</span>
            </Else>
          </If>
        </div>
      );
    }

    return (
      <Ripple className={"field-wrapper fileupload-field"}>
        <div
          ref={this.inputRef}
          className="k-d-flex k-flex-col"
        >
          <If condition={this.label}>
            <Label
              id={this.fieldName + "_label"}
              style={{ fontSize: "14px", fontWeight: "600" }}
            >
              {this.label}
            </Label>
          </If>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Upload
              data-testid={this.fieldName}
              name={this.fieldName}
              batch={false}
              multiple={true}
              files={this.state.files}
              onAdd={this.onAdd}
              onProgress={this.onProgress}
              onStatusChange={this.onStatusChange}
              saveUrl={this.onSaveRequest.bind(this)}
              listItemUI={(e) => this.CustomListItemUI(e)}
              restrictions={{ maxFileSize: 26214400 }}
            />
            <If
              condition={
                this.fieldInformation !== undefined &&
                this.fieldInformation !== "" &&
                this.fieldInformation !== "<p></p>"
              }
            >
              <TooltipPopupIcon content={this.fieldInformation} />
            </If>
          </div>
          <If condition={this.hint}>
            <Hint>{this.hint}</Hint>
          </If>
        </div>
      </Ripple>
    );
  }
}
